// OvernightQueueTab.js
// Overnight Work Queue tab - tasks scheduled for automatic overnight execution
// Extracted from AIWorkforcePage.js for modularity

import React from 'react';
import { TabsContent } from '../../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { formatDistanceToNow, format } from 'date-fns';
import {
  Bot, Plus, Play, RefreshCw, Trash2, Edit, Clock, ListTodo, Activity,
  CheckCircle, XCircle, AlertTriangle, History, Workflow
} from 'lucide-react';

const OvernightQueueTab = ({
  // Queue data
  overnightQueue,
  overnightQueueLogs,
  customProcesses,
  // Running state
  overnightQueueRunning,
  // Task form state for editing
  overnightTaskForm,
  setOvernightTaskForm,
  setEditingOvernightTask,
  // Dialog controls
  onShowAddTaskDialog,
  onShowEditTaskDialog,
  onShowCreateProcessDialog,
  // Actions
  onClearCompleted,
  onRunNow,
  onRemoveTask,
  onClearAllLogs,
  onDeleteLog,
  onQueueProcess,
  onDeleteProcess
}) => {
  const pendingTasks = overnightQueue.tasks?.filter(t => t.status === 'pending') || [];
  const completedOrFailedTasks = overnightQueue.tasks?.filter(t => ['completed', 'failed'].includes(t.status)) || [];

  return (
    <TabsContent value="overnight" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Overnight Work Queue</h2>
          <p className="text-sm text-muted-foreground">
            Queue tasks for automatic execution after 4:30 PM. Tasks run sequentially overnight.
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={onClearCompleted}
            disabled={!overnightQueue.stats?.completed && !overnightQueue.stats?.failed}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear Completed
          </Button>
          <Button 
            variant="outline"
            onClick={onRunNow}
            disabled={overnightQueueRunning || overnightQueue.stats?.pending === 0}
            data-testid="run-queue-now-btn"
          >
            {overnightQueueRunning ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Run Now (Test)
              </>
            )}
          </Button>
          <Button onClick={onShowAddTaskDialog} data-testid="add-overnight-task-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Task
          </Button>
        </div>
      </div>

      {/* Queue Stats */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <StatCard value={overnightQueue.stats?.total || 0} label="Total" colorClass="slate" />
        <StatCard value={overnightQueue.stats?.pending || 0} label="Pending" colorClass="blue" />
        <StatCard value={overnightQueue.stats?.processing || 0} label="Processing" colorClass="amber" />
        <StatCard value={overnightQueue.stats?.completed || 0} label="Completed" colorClass="emerald" />
        <StatCard value={overnightQueue.stats?.failed || 0} label="Failed" colorClass="red" />
        <StatCard value={overnightQueue.stats?.skipped || 0} label="Skipped" colorClass="orange" />
      </div>

      {/* Config Info */}
      {overnightQueue.config && (
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-500" />
                  <span className="text-sm text-slate-600">
                    Trigger Time: <span className="font-medium">{overnightQueue.config.trigger_time || '4:30 PM'}</span>
                  </span>
                </div>
                <Badge variant={overnightQueue.config.is_enabled ? 'default' : 'secondary'}>
                  {overnightQueue.config.is_enabled ? 'Enabled' : 'Disabled'}
                </Badge>
                {overnightQueue.config.is_running && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700">
                    <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                    Running
                  </Badge>
                )}
              </div>
              {overnightQueue.config.last_run_completed && (
                <div className="text-sm text-slate-500">
                  Last run: {formatDistanceToNow(new Date(overnightQueue.config.last_run_completed), { addSuffix: true })}
                  <Badge variant="outline" className="ml-2">
                    {overnightQueue.config.tasks_completed}/{overnightQueue.config.tasks_processed} completed
                  </Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Queue Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Tasks */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ListTodo className="h-5 w-5" />
              Queued Tasks
            </CardTitle>
            <CardDescription>Tasks waiting to be processed overnight</CardDescription>
          </CardHeader>
          <CardContent>
            {pendingTasks.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <ListTodo className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No pending tasks in queue</p>
                <Button variant="link" onClick={onShowAddTaskDialog}>
                  Add a task to get started
                </Button>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {pendingTasks.map((task) => (
                  <PendingTaskCard
                    key={task.id}
                    task={task}
                    onEdit={() => {
                      setEditingOvernightTask(task);
                      setOvernightTaskForm({
                        ...overnightTaskForm,
                        title: task.title,
                        description: task.description,
                        priority: task.priority,
                        agent_type: task.agent_type || 'mixed'
                      });
                      onShowEditTaskDialog();
                    }}
                    onRemove={() => onRemoveTask(task.id)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Execution Logs */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Activity className="h-5 w-5" />
                  Morning Review Logs
                </CardTitle>
                <CardDescription>Overnight execution results and warnings</CardDescription>
              </div>
              {overnightQueueLogs.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-red-600 hover:text-red-700"
                  onClick={onClearAllLogs}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {overnightQueueLogs.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Activity className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No execution logs yet</p>
                <p className="text-xs">Logs will appear after overnight processing runs</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {overnightQueueLogs.map((log) => (
                  <LogCard key={log.id} log={log} onDelete={() => onDeleteLog(log.id)} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Completed/Failed Tasks History */}
      {completedOrFailedTasks.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <History className="h-5 w-5" />
              Recent Results
            </CardTitle>
            <CardDescription>Tasks completed or failed in recent runs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {completedOrFailedTasks.map((task) => (
                <CompletedTaskCard key={task.id} task={task} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Custom Processes Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Workflow className="h-5 w-5" />
                Custom Processes
              </CardTitle>
              <CardDescription>Reusable workflow templates for overnight execution</CardDescription>
            </div>
            <Button size="sm" onClick={onShowCreateProcessDialog} data-testid="create-process-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Process
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {customProcesses.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Workflow className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No custom processes defined</p>
              <p className="text-xs mt-1">Create reusable workflows to queue multiple tasks at once</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {customProcesses.map((process) => (
                <ProcessCard 
                  key={process.id} 
                  process={process} 
                  onQueue={() => onQueueProcess(process.id)}
                  onDelete={() => onDeleteProcess(process.id)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
};

// Stat Card Sub-component
const StatCard = ({ value, label, colorClass }) => (
  <Card className={`bg-gradient-to-br from-${colorClass}-50 to-${colorClass === 'slate' ? 'gray' : colorClass}-50 border-${colorClass}-200`}>
    <CardContent className="pt-4">
      <div className="text-center">
        <p className={`text-3xl font-bold text-${colorClass}-700`}>{value}</p>
        <p className={`text-xs text-${colorClass}-500`}>{label}</p>
      </div>
    </CardContent>
  </Card>
);

// Pending Task Card Sub-component
const PendingTaskCard = ({ task, onEdit, onRemove }) => (
  <div 
    className="p-3 border rounded-lg bg-white hover:bg-slate-50 transition-colors"
    data-testid={`overnight-task-${task.id}`}
  >
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium">{task.title}</span>
          <Badge variant="outline" className="text-xs">P{task.priority}</Badge>
        </div>
        <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{task.description}</p>
        <p className="text-xs text-slate-400 mt-1">
          Added {formatDistanceToNow(new Date(task.created_at), { addSuffix: true })}
        </p>
      </div>
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" onClick={onEdit}>
          <Edit className="h-4 w-4 text-blue-500" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onRemove}>
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  </div>
);

// Log Card Sub-component
const LogCard = ({ log, onDelete }) => {
  const getLogIcon = () => {
    switch (log.event) {
      case 'task_completed': return <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5" />;
      case 'task_failed': return <XCircle className="h-4 w-4 text-red-600 mt-0.5" />;
      case 'queue_started': return <Play className="h-4 w-4 text-blue-600 mt-0.5" />;
      case 'queue_completed': return <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5" />;
      case 'queue_error': return <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />;
      default: return <Activity className="h-4 w-4 text-slate-500 mt-0.5" />;
    }
  };

  const getLogClass = () => {
    if (log.severity === 'error') return 'bg-red-50 border-red-200';
    if (log.severity === 'warning') return 'bg-amber-50 border-amber-200';
    if (log.event === 'task_completed') return 'bg-emerald-50 border-emerald-200';
    return 'bg-white';
  };

  return (
    <div className={`p-3 border rounded-lg group ${getLogClass()}`}>
      <div className="flex items-start gap-2">
        {getLogIcon()}
        <div className="flex-1">
          <p className="text-sm font-medium">{log.details}</p>
          <p className="text-xs text-slate-500 mt-1">
            {format(new Date(log.timestamp), 'MMM d, yyyy h:mm a')}
          </p>
        </div>
        <Button 
          variant="ghost" 
          size="sm"
          className="opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={onDelete}
        >
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
};

// Completed Task Card Sub-component
const CompletedTaskCard = ({ task }) => (
  <div className={`p-3 border rounded-lg ${task.status === 'completed' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-2">
        {task.status === 'completed' ? (
          <CheckCircle className="h-4 w-4 text-emerald-600" />
        ) : (
          <XCircle className="h-4 w-4 text-red-600" />
        )}
        <span className="font-medium">{task.title}</span>
        <Badge variant={task.status === 'completed' ? 'default' : 'destructive'}>
          {task.status}
        </Badge>
      </div>
      <span className="text-xs text-slate-500">
        {task.completed_at && formatDistanceToNow(new Date(task.completed_at), { addSuffix: true })}
      </span>
    </div>
    {task.error_message && (
      <p className="text-sm text-red-600 mt-1">Error: {task.error_message}</p>
    )}
  </div>
);

// Process Card Sub-component
const ProcessCard = ({ process, onQueue, onDelete }) => (
  <div 
    className="p-4 border rounded-lg bg-white hover:shadow-md transition-shadow"
    data-testid={`process-${process.id}`}
  >
    <div className="flex items-start justify-between mb-2">
      <div>
        <h4 className="font-medium">{process.name}</h4>
        <p className="text-xs text-muted-foreground line-clamp-2">{process.description || 'No description'}</p>
      </div>
      <Badge variant="outline" className="text-xs">
        {process.tasks?.length || 0} tasks
      </Badge>
    </div>
    
    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
      <span>Executed {process.execution_count || 0}x</span>
      {process.last_executed && (
        <span>• Last: {formatDistanceToNow(new Date(process.last_executed), { addSuffix: true })}</span>
      )}
    </div>
    
    <div className="flex gap-2">
      <Button 
        size="sm" 
        className="flex-1"
        onClick={onQueue}
        data-testid={`queue-process-${process.id}`}
      >
        <ListTodo className="h-3 w-3 mr-1" />
        Queue All
      </Button>
      <Button 
        size="sm" 
        variant="ghost"
        onClick={onDelete}
      >
        <Trash2 className="h-4 w-4 text-red-500" />
      </Button>
    </div>
  </div>
);

export default OvernightQueueTab;
